/**
 * Student login (spec 8.2, 9.6; prototype A's login flow): class number on a numeric pad, then
 * a tile grid of that class's usernames with their emoji avatars, then a 4-dot PIN pad that
 * submits itself on the fourth digit.
 *
 * Listing a class's usernames to anyone who knows the number leaks nothing — they are
 * pseudonymous (spec 8.2) — which is what lets a 10-year-old log in with two taps and a PIN.
 * The last class number is the only UI preference we keep (ADR 0005).
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { publicJson } from "../shared/api.js";
import type { ClassStudent, LoginResponse } from "../shared/api-types.js";
import { startSession } from "../shared/auth-store.js";
import { ApiClientError, germanMessage } from "../shared/errors.js";
import { storage } from "../shared/storage.js";
import { avatarFor } from "../shared/avatars.js";
import { Button } from "../shared/primitives/Button.js";
import { BackspaceIcon, CheckIcon, ChevronLeftIcon } from "../shared/primitives/Icons.js";

type Step = "class" | "who" | "pin";

const MAX_CLASS_DIGITS = 4;
const PIN_LENGTH = 4;

export function LoginScreen() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("class");
  const [classNumber, setClassNumber] = useState("");
  const [students, setStudents] = useState<ClassStudent[]>([]);
  const [selected, setSelected] = useState<ClassStudent | null>(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    void storage.get("lastClassNumber").then((value) => {
      if (value) setClassNumber(value);
    });
  }, []);

  async function loadClass() {
    if (!classNumber) return;
    setPending(true);
    setError(null);
    try {
      const list = await publicJson<ClassStudent[]>("/classes/" + classNumber + "/students");
      if (list.length === 0) {
        setError("In dieser Klasse ist noch niemand angelegt.");
        return;
      }
      setStudents(list);
      setStep("who");
    } catch (err) {
      setError(
        err instanceof ApiClientError && err.code === "not_found"
          ? "Diese Klasse gibt es nicht."
          : germanMessage(err),
      );
    } finally {
      setPending(false);
    }
  }

  async function submitPin(fullPin: string) {
    if (!selected) return;
    setPending(true);
    setError(null);
    try {
      const response = await publicJson<LoginResponse>("/auth/student/login", {
        method: "POST",
        body: JSON.stringify({
          classNumber: Number(classNumber),
          username: selected.username,
          pin: fullPin,
        }),
      });
      await startSession("student", response.accessToken, response.refreshToken);
      await storage.set("lastClassNumber", classNumber);
      void navigate("/", { replace: true });
    } catch (err) {
      setPin("");
      setShake(true);
      setTimeout(() => setShake(false), 400);
      setError(germanMessage(err));
    } finally {
      setPending(false);
    }
  }

  function pressPin(digit: string) {
    if (pending || pin.length >= PIN_LENGTH) return;
    const next = pin + digit;
    setPin(next);
    setError(null);
    if (next.length === PIN_LENGTH) setTimeout(() => void submitPin(next), 150);
  }

  return (
    <div
      className="flex h-full flex-col px-6 pt-12 pb-8"
      style={{
        paddingTop: "calc(3rem + env(safe-area-inset-top, 0px))",
        paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))",
      }}
    >
      {step === "class" && (
        <div className="flex flex-1 flex-col">
          <h1 className="m-0 mb-1 text-[30px] font-black text-white">Hallo!</h1>
          <p className="m-0 mb-7 text-white opacity-65">In welche Klasse gehst du?</p>
          <div className="mb-[18px] min-h-24 rounded-[20px] bg-[var(--color-tile)] p-[18px] text-center text-[48px] font-black tracking-[.1em] text-white">
            {classNumber || " "}
          </div>
          {error && <p className="mb-3 text-[15px] font-bold text-[var(--color-bad)]">{error}</p>}
          <Pad
            onDigit={(digit) =>
              setClassNumber((value) => (value.length < MAX_CLASS_DIGITS ? value + digit : value))
            }
            onBackspace={() => setClassNumber((value) => value.slice(0, -1))}
            trailing={
              <Button
                onClick={() => void loadClass()}
                disabled={!classNumber || pending}
                aria-label="Weiter"
                className="grid place-items-center rounded-[18px] bg-[var(--color-accent)] py-4 text-black disabled:opacity-40"
              >
                <CheckIcon />
              </Button>
            }
          />
        </div>
      )}

      {step === "who" && (
        <div className="flex min-h-0 flex-1 flex-col">
          <Button
            onClick={() => setStep("class")}
            className="mb-3.5 flex items-center gap-1 self-start text-[15px] text-white opacity-60"
          >
            <ChevronLeftIcon size={16} /> Klasse {classNumber}
          </Button>
          <h1 className="m-0 mb-1 text-[30px] font-black text-white">Wer bist du?</h1>
          <p className="m-0 mb-7 text-white opacity-65">Tippe auf deinen Namen.</p>
          {/* `auto-rows-min` keeps the tiles at their own height: the grid is `flex-1` to take the
              rest of the screen and scroll a class of 25, but without it a class with one row
              stretches that row down the whole screen. */}
          <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-3 content-start gap-2.5 overflow-y-auto pb-3">
            {students.map((student) => (
              <Button
                key={student.username}
                onClick={() => {
                  setSelected(student);
                  setPin("");
                  setError(null);
                  setTimeout(() => setStep("pin"), 180);
                }}
                className={
                  "rounded-2xl px-1.5 py-[18px] text-center text-[14px] font-bold " +
                  (selected?.username === student.username
                    ? "bg-[var(--color-accent)] text-black"
                    : "bg-[var(--color-tile)] text-white")
                }
              >
                <span className="mb-1.5 block text-[26px]" aria-hidden="true">
                  {avatarFor(student.number)}
                </span>
                {student.username}
              </Button>
            ))}
          </div>
        </div>
      )}

      {step === "pin" && selected && (
        <div className="flex flex-1 flex-col">
          <Button
            onClick={() => {
              setStep("who");
              setPin("");
              setError(null);
            }}
            className="mb-3.5 flex items-center gap-1 self-start text-[15px] text-white opacity-60"
          >
            <ChevronLeftIcon size={16} /> zurück
          </Button>
          <div className="mb-1.5 text-[14px] text-white opacity-70">
            Angemeldet als
            <b className="block text-[22px] opacity-100">{selected.username}</b>
          </div>
          <h1 className="m-0 mb-1 text-[30px] font-black text-white">Dein PIN</h1>
          <div
            className={"my-2 mb-7 flex justify-center gap-3 " + (shake ? "pin-shake" : "")}
            role="status"
            aria-label={"PIN, " + pin.length + " von " + PIN_LENGTH + " Ziffern"}
          >
            {Array.from({ length: PIN_LENGTH }, (_, index) => (
              <i
                key={index}
                className={
                  "h-[18px] w-[18px] rounded-full border-2 border-white " +
                  (index < pin.length ? "bg-white" : "")
                }
              />
            ))}
          </div>
          {error && (
            <p className="mb-3 text-center text-[15px] font-bold text-[var(--color-bad)]">
              {error}
            </p>
          )}
          <Pad
            onDigit={pressPin}
            onBackspace={() => setPin((value) => value.slice(0, -1))}
            backspaceLast
          />
        </div>
      )}
    </div>
  );
}

/** The shared numeric pad: 1–9, then either ⌫ 0 ✓ (class) or ⌫ placed last (PIN). */
function Pad({
  onDigit,
  onBackspace,
  trailing,
  backspaceLast,
}: {
  onDigit: (digit: string) => void;
  onBackspace: () => void;
  trailing?: React.ReactNode;
  backspaceLast?: boolean;
}) {
  const key = (label: string) => (
    <Button
      key={label}
      onClick={() => onDigit(label)}
      className="rounded-[18px] bg-[var(--color-tile)] py-4 text-[26px] font-bold text-white"
    >
      {label}
    </Button>
  );
  const backspace = (
    <Button
      key="backspace"
      onClick={onBackspace}
      aria-label="Löschen"
      className="grid place-items-center rounded-[18px] bg-[var(--color-tile)] py-4 text-white"
    >
      <BackspaceIcon />
    </Button>
  );

  return (
    <div className="mt-auto grid grid-cols-3 gap-2.5">
      {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map(key)}
      {backspaceLast ? (
        <>
          <span />
          {key("0")}
          {backspace}
        </>
      ) : (
        <>
          {backspace}
          {key("0")}
          {trailing}
        </>
      )}
    </div>
  );
}
